// Pure formatting helpers live in examples-format.ts (W-J seam extraction).
// examples.ts imports them here; examples-format.ts is the source of truth.
import { classForStandard, defectTier, fileIconHtml, filterClaimsForDisplay, flattenChanges, fmtDate, fmtProcessingTime, formatLabel, remediationClaimsFor, renderStandardsBadgeRow, unitWord, REMEDIATION_CLAIMS_BY_FIXTURE, } from './examples-format.js';
// Pure non-formatting helpers live in examples-logic.ts (ts-coverage-260626 seam).
// examples.ts imports them here; examples-logic.ts is the source of truth for
// AWAITING_ITEMS_BY_FIXTURE, groupSamplesByKind (SSOT-derived kind grouping),
// computeDefectDeltaPct, and awaitingItemMatchesFilters.
import { AWAITING_ITEMS_BY_FIXTURE, groupSamplesByKind, computeDefectDeltaPct, awaitingItemMatchesFilters, } from './examples-logic.js';
// The shared by-kind supported-format bubbles renderer (rule #11 / A.6) — the
// SAME builder the SPA landing + /editor empty state use. /examples is its third
// consumer: the "How to use" affordance card's format chips.
import { renderSupportedFormatBubblesInto } from './shared/supported-format-bubbles.js';
// The SINGLE ext→editor-surface routing contract (rule #11 / A.6), shared with
// the SPA job cards (spa/renderers.ts). Each example handle is `examples:<id>`
// (the static-example-id seam the editor backend already understands for PDF,
// PPTX, DOCX, and XLSX). A format outside the closed routing map → null → no button.
import { editorRouteForExt, editorHrefForExt, } from './shared/editor-routing.js';
// "Green per Microsoft" per-sample badge builder (examples-msparity-datapath-260802
// — MS-parity Epic Phase 4 /examples follow-up). Pure data→HTML builder, module-scope
// exported from standards-coverage.ts specifically so this production call site can
// reach it (NOT the __test-only escape hatch, which is tree-shaken from delivery).
import { renderMicrosoftParityBadge } from './standards-coverage.js';
/**
 * Test-only escape hatch — exposes IIFE-private functions for jsdom pilot tests.
 *
 * WHY module-level mutable object: renderSampleCard and loadCatalog are defined
 * inside the IIFE (required for the esc/escAttr bindings). The IIFE writes to
 * this object on execution so jsdom tests that import this module can reach
 * the functions without refactoring the closure.
 *
 * WHY optional types: at import time (before the IIFE runs) the fields are
 * undefined; by the time any test body executes the IIFE has already run.
 * Tests MUST assert non-null before calling (see examples.jsdom.test.ts).
 *
 * Per docs/epics/active/ts-jsdom-coverage-260626/main.md Phase 2 Pilot C.
 */
export const __test = {};
// AwaitingItem is imported from examples-logic.ts (ts-coverage-260626 seam).
/**
 * /examples page — fetch catalog and render sample cards.
 *
 * Design ref: docs/design/examples-page-260507.md §"API shape" + §"HTML / JS shape".
 * Visual contract: tmp/examples-mockups-260507/v4/index.html
 *
 * Card shape mirrors app.js:renderDoneCard. Render functions produce DOM from
 * the catalog.json sample objects; no template library, no framework.
 *
 * Phase 2 (260512): per-change standards attribution. examples.js fetches
 * /api/rules/metadata once and joins it with each change's rule_ids[] to
 * render per-change standards badges + a per-analysis rollup. Join helpers
 * extracted to /static/rule-metadata-helpers.js (window.AdaRuleMetadata)
 * so the downloadable-changelog renderer can reuse them.
 * See docs/design/examples-phase2-per-change-standards-260512.md.
 */
(function () {
    "use strict";
    // ─── Rule metadata (Phase 2) ─────────────────────────────────────────────
    //
    // The fetch/cache and join helpers live in /static/rule-metadata-helpers.js
    // (window.AdaRuleMetadata) so the downloadable-changelog renderer can
    // share the same code. Re-bind the names locally so the rest of this
    // module reads the same as before the extraction.
    // Load order: rule-metadata-helpers.js MUST load before examples.js
    // (enforced by examples.html script order).
    const _AdaRM = window.AdaRuleMetadata;
    if (!_AdaRM) {
        // Load-order invariant: rule-metadata-helpers.js MUST load before examples.js
        // (enforced by examples.html script order). Fail loud if the contract breaks.
        throw new Error('examples.js: window.AdaRuleMetadata missing — rule-metadata-helpers.js must load first');
    }
    const fetchRuleMetadata = _AdaRM.fetchRuleMetadata;
    const standardsForRuleIds = _AdaRM.standardsForRuleIds;
    const targetLabel = _AdaRM.targetLabel;
    const rollupStandardsByTarget = _AdaRM.rollupStandardsByTarget;
    // AWAITING_ITEMS_BY_FIXTURE is imported from examples-logic.ts (ts-coverage-260626 seam).
    // When the backend wires real awaiting_items[] onto catalog/changes envelopes, replace
    // AWAITING_ITEMS_BY_FIXTURE with `sample.awaiting_items` / `envelope.awaiting_items`.
    // classForStandard, filterClaimsForDisplay, renderStandardsBadgeRow,
    // REMEDIATION_CLAIMS_BY_FIXTURE, remediationClaimsFor, fileIconHtml,
    // fmtProcessingTime, and defectTier are imported from examples-format.ts.
    // ─── Render one sample card (mirrors renderDoneCard in app.js) ───────────
    function renderSampleCard(sample) {
        const id = sample.id;
        const format = (sample.format || "pdf").toLowerCase();
        const filename = sample.filename || `${id}.${format}`;
        const source = sample.source_attribution || sample.source || "";
        const pages = sample.pages || 0;
        const pagesUnit = format === "xlsx" ? "sheets" : (format === "pptx" ? "slides" : "pages");
        // Defects before/after may be null when the a11y-check service was
        // unavailable at emit time. Render them only when BOTH values are
        // available (the "→ defects" arrow notation is misleading with one
        // side missing). When unavailable, skip the row contents but keep
        // the row to preserve uniform card height.
        const defBefore = sample.defects_before;
        const defAfter = sample.defects_after;
        const haveDefects = (defBefore != null && defAfter != null);
        const tier = haveDefects ? defectTier(defBefore, defAfter) : null;
        const tierCls = tier ? ` fix-count-${tier}` : "";
        // R3 (examples-served-page-quality-261004): a WAIVED null count (the checker
        // could not honestly count a corrupt input) renders a DISTINCT string with a
        // DISTINCT class — NOT the gated "defect count unavailable". This keeps that
        // phrase a true-positive-only failure marker the EX-DC-3 browser gate keys on,
        // so a sanctioned waiver never false-fails the gate.
        const defWaived = sample.defects_waived;
        const defectsHtml = haveDefects
            ? `<span class="fix-count"><strong class="${tierCls.trim()}">${escHtml(String(defBefore))} → ${escHtml(String(defAfter))}</strong> defects</span>`
            : defWaived
                ? `<span class="fix-count fix-count-waived" title="Defect count not applicable — the source file is irregular, so an honest count could not be produced">defect count not applicable — source file irregular</span>`
                : `<span class="fix-count fix-count-na" title="Defect counts not available for this sample">defect count unavailable</span>`;
        // Processing time — show "processed in Xs" when ms is available + > 0.
        // Per-page rate shown as smaller secondary text for throughput context.
        const procTime = fmtProcessingTime(sample.processing_ms);
        let procHtml = "";
        if (procTime) {
            const perPage = (pages > 0 && sample.processing_ms)
                ? fmtProcessingTime(Math.round(sample.processing_ms / pages))
                : null;
            const perPageHtml = perPage
                ? `<span class="proc-per-page">${escHtml(perPage)}/${pagesUnit.replace(/s$/, "")}</span>`
                : "";
            procHtml = `<span class="proc-time">processed in ${escHtml(procTime)}${perPageHtml ? ` · ${perPageHtml}` : ""}</span>`;
        }
        // "Green per Microsoft" badge — additive + null-safe: absent for pdf and
        // any format/sample without an authored parity map (older catalog
        // entries pre-dating this field simply have no `microsoft_parity` key).
        const msParityHtml = sample.microsoft_parity
            ? `<div class="job-msparity-row">${renderMicrosoftParityBadge(sample.microsoft_parity)}</div>`
            : "";
        const beforeUrl = `/api/examples/${encodeURIComponent(id)}/before`;
        const afterUrl = `/api/examples/${encodeURIComponent(id)}/after`;
        const changesUrl = `/api/examples/${encodeURIComponent(id)}/changes`;
        // WHY per-link aria-label: the visible text "Changes", "Before",
        // "After", "Editor" repeats across every sample card, so a screen-
        // reader user navigating the page hears N identical "Changes" links
        // with no context. Suffixing each accessible name with the filename
        // gives WCAG 4.1.2-compliant uniqueness while keeping the visible
        // text short. The aria-label begins with the visible text so WCAG
        // 2.5.3 (label-in-name) holds.
        const labelSuffix = `for ${filename}`;
        // The "Editor" button opens the format-appropriate review surface via the
        // shared routing contract: PDF → the WASM /editor; PPTX/DOCX/XLSX → the O365
        // accessibility editor /o365-view. Every format in the closed routing map
        // resolves to a slot; an unmapped ext (rare/legacy) → no route → slot
        // omitted (rendered absent, never a dead disabled control). The example
        // handle is `examples:<id>` — the static-example-id seam the editor backend
        // serves for all three formats. `data-editor-surface` is the stable a11y/e2e
        // selector.
        const editorRoute = editorRouteForExt(`.${format}`);
        const editorUrl = editorHrefForExt(`.${format}`, `examples:${id}`);
        const editorBtn = (editorRoute && editorUrl)
            ? `<a href="${escAttr(editorUrl)}" class="btn editor-btn" target="_blank" rel="noopener" data-editor-surface="${escAttr(editorRoute.surface)}" title="${editorRoute.surface === "wasm-pdf" ? "Open in PDF editor" : "Open in accessibility view"}" aria-label="Editor ${escHtml(labelSuffix)}">
           <svg class="edit-icon" aria-hidden="true" focusable="false"><use href="#icon-edit"/></svg> Editor
         </a>`
            : "";
        const article = document.createElement("article");
        article.className = "job-card";
        article.setAttribute("role", "listitem");
        // Three-row card template: filename (with size hint), defects + source
        // attribution, and the action button row. Every card uses this exact
        // structure regardless of format — only values vary.
        // Anchor target id for the per-sample expanded analysis below the grid.
        // Single-source: must match the article id rendered in renderExpandedArticle.
        const analysisAnchorId = `analysis-${id}`;
        article.innerHTML = `
      <div class="job-filename">
        ${fileIconHtml(format)}
        <span class="filename-text" title="${escHtml(filename)}">${escHtml(filename)}</span>
        <span class="job-filesize">${pages} ${escHtml(unitWord(format, pages))}</span>
      </div>
      <div class="job-meta-row">
        ${defectsHtml}
        <span class="source-attrib" title="${escHtml(source)}">${escHtml(source)}</span>
      </div>
      ${procHtml ? `<div class="job-proc-row">${procHtml}</div>` : ""}
      ${msParityHtml}
      <div class="job-actions-row" role="group" aria-label="Actions for ${escHtml(labelSuffix)}">
        <a href="${changesUrl}" class="btn btn-link" data-changes-link="${escHtml(id)}" title="Download the formatted accessibility change report for this sample" aria-label="Changes ${escHtml(labelSuffix)}">Changes</a>
        <a href="${beforeUrl}" class="btn btn-outline" title="Download the original (un-remediated) file" aria-label="Before ${escHtml(labelSuffix)}">Before</a>
        <a href="${afterUrl}" class="btn btn-primary" title="Download the remediated file" aria-label="After ${escHtml(labelSuffix)}">After</a>
        ${editorBtn}
      </div>
      <div class="job-analysis-link">
        <a href="#${escAttr(analysisAnchorId)}" class="btn btn-link analysis-anchor" aria-label="Full analysis ↓ ${escHtml(labelSuffix)}">Full analysis &darr;</a>
      </div>
    `;
        // Wire the Changes link to fetch+format+download instead of
        // navigating to raw JSON.
        const changesLink = article.querySelector("[data-changes-link]");
        if (changesLink) {
            changesLink.addEventListener("click", (ev) => handleChangesClick(ev, sample));
        }
        return article;
    }
    // Kind-heading icon: the icon of the kind's canonical native format
    // (documents→docx, presentations→pptx, spreadsheets→xlsx, pdf→pdf). Reuses
    // fileIconHtml so the sprite reference matches the cards. "typesetting" never
    // reaches the catalog gallery (no catalog sample carries a typesetting format),
    // so its icon is a harmless fallback pending the drive-latex-typst SSOT widening.
    const _KIND_ICON_FORMAT = {
        documents: "docx",
        presentations: "pptx",
        spreadsheets: "xlsx",
        pdf: "pdf",
        typesetting: "pdf",
    };
    function kindIconHtml(kind) {
        return fileIconHtml(_KIND_ICON_FORMAT[kind] ?? "pdf");
    }
    // ─── Expanded-analysis rendering (per sample) ────────────────────────────
    //
    // Renders one <article class="showcase-example"> per sample, populated from
    // the sample's /api/examples/{id}/changes envelope. Articles are appended
    // to #expanded-analyses below the card grid. Anchor target ids on each
    // article match the "Full analysis ↓" hrefs on the cards.
    //
    // Data shape (changes envelope):
    //   { report: {
    //       total_items|total_changes,
    //       categories: [ { category|label, count, changes: [
    //           { page_or_slide|slide, description|message,
    //             rule_ids: [...], category?, ... } ] } ]
    //     } }
    // rule_ids[] are still parsed off each change for future use (the
    // window.adaExamples helpers expose standardsForRuleIds /
    // rollupStandardsByTarget for downstream consumers), but the
    // per-change std-chip badges and the per-analysis Standards-advanced
    // rollup were dropped from the rendered DOM per mockup feedback
    // 260512 — repetitive across a single-format analysis.
    // formatLabel and unitWord imported from examples-format.ts.
    // Render one change row inside the sample-changes block.
    //
    // Phase A (260513): per-change standards badges are sourced from the
    // change's stamped `category` field (set by flattenChanges) via
    // REMEDIATION_CLAIMS_BY_FIXTURE. Earlier v1 dropped per-change badges
    // because they were repetitive across a single-format analysis; the
    // sole-source filter (Q2 settlement) addresses the noise concern by
    // suppressing DocAble when external claims are present. The badge row
    // is rendered ONLY when the (fixtureId, category) lookup yields claims
    // — categories absent from the demo map continue rendering badge-less.
    function renderChangeEntry(change, format, fixtureId) {
        const slide = change.page_or_slide != null ? change.page_or_slide
            : change.slide != null ? change.slide
                : null;
        const locWord = format === "pptx" ? "Slide"
            : format === "xlsx" ? "Sheet"
                : "Page";
        const loc = slide != null ? `${locWord} ${escHtml(String(slide))}` : "Document level";
        const desc = change.description || change.message || "Change applied";
        const claims = fixtureId ? remediationClaimsFor(fixtureId, change.category || "") : [];
        const badges = renderStandardsBadgeRow(claims);
        const badgeRowHtml = badges
            ? `<div class="change-standards">${badges}</div>`
            : "";
        return `
      <div class="sample-change-entry">
        <div class="change-loc">${loc}</div>
        <div class="change-desc">${escHtml(String(desc))}</div>
        ${badgeRowHtml}
      </div>
    `;
    }
    // flattenChanges imported from examples-format.ts.
    // Sample-change-entry cap: too many entries here would dilute the page;
    // the user can download the full changelog from the action row above.
    const MAX_SAMPLE_CHANGES = 6;
    function renderExpandedArticle(sample, envelope) {
        const id = sample.id;
        const format = (sample.format || "pdf").toLowerCase();
        const title = sample.title || sample.filename || id;
        const pages = sample.pages || 0;
        const source = sample.source_attribution || sample.source || "";
        const report = (envelope && envelope.report) || {};
        const cats = Array.isArray(report.categories) ? report.categories : [];
        const totalChanges = report.total_items != null ? report.total_items
            : report.total_changes != null ? report.total_changes : 0;
        const allChanges = flattenChanges(cats);
        // Defect summary (per-sample defects_before/_after pulled from the
        // catalog entry; the envelope itself doesn't carry these counts).
        const defBefore = sample.defects_before;
        const defAfter = sample.defects_after;
        const haveDefects = (defBefore != null && defAfter != null);
        const tier = haveDefects ? defectTier(defBefore, defAfter) : null;
        const tierCls = tier ? ` score-after-${tier}` : "";
        // computeDefectDeltaPct is a pure function imported from examples-logic.ts
        // (ts-coverage-260626 seam). Returns null when before=0 or either value absent.
        const _rawDeltaPct = haveDefects ? computeDefectDeltaPct(defBefore, defAfter) : null;
        const deltaPct = _rawDeltaPct != null
            ? `(${_rawDeltaPct >= 0 ? "−" : "+"}${Math.abs(_rawDeltaPct)}%)`
            : null;
        // Article header. The example-num holds the per-sample ordinal + format
        // icon (mockup parity); seq is filled in by the caller via the second
        // argument to renderExpandedArticle's outer wrapper.
        const fileIcon = `<svg class="file-icon" aria-hidden="true" focusable="false"><use href="#${({ pdf: "icon-pdf", pptx: "icon-pptx", docx: "icon-docx", xlsx: "icon-xlsx" }[format] || "icon-pdf")}"/></svg>`;
        // Defect summary block. Hidden when neither defects nor changes are
        // present (the "no data" branch falls through to .expanded-analysis-empty
        // below). When defects are absent but changes are present, we still
        // render the totals row so the changelog footprint is visible.
        let defectSummaryHtml = "";
        if (haveDefects || totalChanges > 0) {
            const rows = [];
            if (haveDefects) {
                rows.push(`
          <div class="score-row">
            <dt>Accessibility defect count</dt>
            <dd>
              <span class="score-before">${escHtml(String(defBefore))} before</span>
              <span class="score-arrow" aria-hidden="true">&rarr;</span>
              <span class="score-after${tierCls}">${escHtml(String(defAfter))} after</span>
              ${deltaPct ? `<span class="score-delta" aria-label="net change">${escHtml(deltaPct)}</span>` : ""}
            </dd>
          </div>
        `);
            }
            if (totalChanges > 0) {
                const unit = unitWord(format, pages);
                const pagesNote = pages > 0 ? ` across ${pages} ${unit}` : "";
                rows.push(`
          <div class="score-row">
            <dt>Total accessibility changes applied</dt>
            <dd><span style="font-weight:700">${escHtml(String(totalChanges))}</span> change${totalChanges === 1 ? "" : "s"}${escHtml(pagesNote)}</dd>
          </div>
        `);
            }
            defectSummaryHtml = `
        <h3>Defect summary</h3>
        <dl class="score-deltas">${rows.join("")}</dl>
      `;
        }
        // Changes-by-category table. Omitted when no categories present.
        //
        // Phase A: per-category standards badges sourced from
        // REMEDIATION_CLAIMS_BY_FIXTURE. The renderer applies the sole-source
        // display filter (Q2 settlement, 260513) before emitting badges — when
        // a category carries both external and DocAble claims, only the
        // external badges are shown. Backend wiring (real rule.Standards out
        // through /api/examples/{id}/changes) is a follow-up in Phase B+C+D.
        let catTableHtml = "";
        if (cats.length > 0) {
            const rows = cats
                .filter((c) => (c.count || (Array.isArray(c.changes) ? c.changes.length : 0)) > 0)
                .map((c) => {
                const count = c.count || (Array.isArray(c.changes) ? c.changes.length : 0);
                const catLabel = c.label || c.category || "Other";
                const desc = c.description || "";
                const claims = remediationClaimsFor(id, catLabel);
                // Store the FILTERED (sole-source) claim list on the <tr> as a
                // data-standards attribute — the same queryable channel the awaiting
                // items use (renderAwaitingItem), so the VC spec reads the standards off
                // the row (examples-collapsibles.spec.mjs:431/439) and the sole-source
                // invariant is checkable per-row. renderStandardsBadgeRow applies the
                // identical filter, so the badges and the attribute never disagree.
                const filteredClaims = filterClaimsForDisplay(claims);
                const badges = renderStandardsBadgeRow(claims);
                const stdsCell = badges
                    ? `<td class="cat-standards">${badges}</td>`
                    : `<td class="cat-standards"></td>`;
                return `<tr data-standards="${escAttr(filteredClaims.join(","))}"><td>${escHtml(catLabel)}</td><td>${escHtml(desc)}</td>${stdsCell}<td class="cat-count">${escHtml(String(count))}</td></tr>`;
            });
            if (rows.length > 0) {
                catTableHtml = `
          <h3>Changes by category</h3>
          <table class="cat-breakdown" aria-label="Categories of changes applied">
            <thead><tr><th>Category</th><th>Description</th><th>Standards</th><th>Count</th></tr></thead>
            <tbody>${rows.join("")}</tbody>
          </table>
        `;
            }
        }
        // Sample changes section. Each entry's badges (Phase A, 260513) are
        // sourced from REMEDIATION_CLAIMS_BY_FIXTURE keyed by (id, ch.category).
        let sampleChangesHtml = "";
        if (allChanges.length > 0) {
            const slice = allChanges.slice(0, MAX_SAMPLE_CHANGES);
            const entries = slice.map((ch) => renderChangeEntry(ch, format, id)).join("");
            const more = allChanges.length > MAX_SAMPLE_CHANGES
                ? `<p class="score-tool-note">Showing ${slice.length} of ${allChanges.length} changes. Download the full changelog below for the complete list.</p>`
                : "";
            sampleChangesHtml = `<h3>Sample changes</h3>${entries}${more}`;
        }
        // Wrap the changes-by-category table + sample changes in a single
        // default-closed <details> labeled "Remediations performed". Mockup
        // contract: docs/design/mockups/examples-remediations-vs-awaiting-260513.html.
        // When both sub-sections are empty we omit the <details> wrapper
        // entirely (no point in showing an empty collapsible).
        let remediationsHtml = "";
        if (catTableHtml || sampleChangesHtml) {
            const countLabel = totalChanges > 0
                ? `${totalChanges} change${totalChanges === 1 ? "" : "s"}`
                : "";
            // a11y (Phase 6, a11y-name-and-selector-discipline-260629): role="status"
            // + aria-label promotes the count badge to a named accessibility-tree node.
            // Before this, the badge was a bare <span> with no role — invisible to the
            // a11y tree and forcing the spec to read it via a raw .locator(".section-
            // count-badge"). role="status" announces the count to AT (a polite live
            // region) AND makes it queryable via getByRole("status", { name: ... }).
            // aria-label restates the count with the section context so each badge has a
            // self-describing, disambiguated name.
            const countBadge = countLabel
                ? `<span class="section-count-badge" role="status" aria-label="Remediations: ${escAttr(countLabel)}">${escHtml(countLabel)}</span>`
                : "";
            // a11y (Phase 6, a11y-name-and-selector-discipline-260629): give the
            // <details> an explicit aria-label so the disclosure group carries an
            // accessible name EVEN WHEN CLOSED. Chromium's a11y tree does not expose a
            // closed <details>'s <summary> subtree as the group's accessible name, so
            // without aria-label a closed section is unnamed in the a11y tree (the gap
            // that forced spec escapes to fall back to a CSS class locator). The label
            // text matches the visible <summary> so sighted + AT users hear the same name;
            // the count badge is intentionally omitted from the label (the badge is a
            // separately-announced role="status" node — see countBadge below).
            // a11y (Phase 22 VC fix, 260704): explicit role="button" on the <summary>.
            // A <summary> IS the disclosure control, but Chromium's a11y tree stopped
            // exposing a bare <summary> inside an aria-labelled <details> as a `button`
            // role — it flattens to `text` (aria-snapshot shows `text: ▶ Remediations
            // performed` with NO button node). That broke getByRole("button", {name:
            // /Remediations performed/i}) in the VC collapsibles spec (3× deterministic
            // TimeoutError, examples-collapsibles.spec.mjs:186/400). role="button"
            // restores the queryable button node AND is semantically accurate (summary
            // toggles the disclosure) — verified against the live page.
            //
            // a11y (Phase 22 U7-v3 fix, 260704): the SUMMARY's accessible name MUST be
            // UNIQUE per card. With role="button" on every summary, each summary is an
            // interactive control the VC harness's U7-v3 checkUniqueness predicate
            // (predicates.mjs assertAriaLabelQuality) audits page-wide: it reads the
            // summary's accessible name (aria-label > visible text) and flags groups of
            // same-named controls in DIFFERENT page regions. Across N job cards, every
            // summary's visible text is the identical "Remediations performed…" → U7-v3
            // fails (verified against the REAL predicate with pw 1.59 — NOT axe; axe's
            // WCAG-tag rule set does NOT flag this at all). Two constraints bind the fix,
            // both verified against the real predicate:
            //   • U7-v3 (uniqueness): the name must differ per card → append " — {id}".
            //   • U7-v1 (label-in-name, WCAG 2.5.3): when a control has BOTH a visible
            //     label AND an aria-label, the aria-label MUST CONTAIN the visible label
            //     as a substring. The summary's visible label is "Remediations
            //     performed" + the (role=status) count badge text, so the aria-label
            //     must reproduce that exact prefix — hence `Remediations performed` +
            //     countLabel, THEN the " — {id}" disambiguator. Putting the suffix on the
            //     <details> alone does NOT fix U7-v3 (it doesn't change the summary
            //     CONTROL's name), and an aria-label that drops the badge text FAILS
            //     U7-v1. The label still STARTS with "Remediations performed" so the VC
            //     specs' getByRole("button"/"group", {name:/Remediations performed/i})
            //     match.
            const remSummaryAccName = `Remediations performed${countLabel} — ${id}`;
            remediationsHtml = `
        <details class="collapsible-section remediations-section" id="collapsible-remediations-${escAttr(id)}" aria-label="Remediations performed — ${escAttr(id)}">
          <summary role="button" aria-label="${escAttr(remSummaryAccName)}">Remediations performed${countBadge}</summary>
          <div class="collapsible-section-body">
            ${catTableHtml}
            ${sampleChangesHtml}
          </div>
        </details>
      `;
        }
        // "Awaiting human intervention" <details>. Built from per-fixture
        // hardcoded data (AWAITING_ITEMS_BY_FIXTURE) until the standards-bridge
        // wires real data through the catalog envelope. When the fixture has
        // no awaiting items (or is not in the demonstration map), the section
        // is omitted entirely.
        const awaitingItems = AWAITING_ITEMS_BY_FIXTURE[id] || [];
        let awaitingHtml = "";
        if (awaitingItems.length > 0) {
            awaitingHtml = renderAwaitingSection(id, awaitingItems);
        }
        // Empty-state branch when the envelope is missing OR has neither
        // defects nor changes. Mirrors graceful-degradation behaviour.
        const bodyEmptyHtml = (defectSummaryHtml === "" && remediationsHtml === "" && awaitingHtml === "")
            ? `<div class="expanded-analysis-empty">Full changelog not available for this sample.</div>`
            : "";
        // Thumbnail (rendered only when sample carries screenshot_url —
        // future render-service Phase 1a will populate this field).
        const thumb = sample.screenshot_url && typeof sample.screenshot_url === "string"
            ? `<figure class="example-thumbnail-wrap">
           <img class="example-thumbnail" src="${escAttr(sample.screenshot_url)}"
                alt="First-page thumbnail of ${escAttr(title)}"
                loading="lazy">
           <figcaption class="example-thumbnail-cap">Preview</figcaption>
         </figure>`
            : "";
        // "Open in editor" — format-appropriate review surface via the shared
        // routing contract (PDF → /editor; PPTX/DOCX/XLSX → /o365-view).
        // Placed in the header action row between After and Download full changelog
        // so the mockup's button order is preserved:
        //   Before · After (remediated) · Open in editor · Download full changelog · ↑ Back to top
        const editorRoute = editorRouteForExt(`.${format}`);
        const editorHref = editorHrefForExt(`.${format}`, `examples:${id}`);
        const editorBtn = (editorRoute && editorHref)
            ? `<a href="${escAttr(editorHref)}" class="btn editor-btn" target="_blank" rel="noopener" data-editor-surface="${escAttr(editorRoute.surface)}" title="${editorRoute.surface === "wasm-pdf" ? "Open in PDF editor" : "Open in accessibility view"}" aria-label="Open in editor for ${escHtml(title)}">
           <svg class="edit-icon" aria-hidden="true" focusable="false"><use href="#icon-edit"/></svg> Open in editor
         </a>`
            : "";
        // Format-specific aside (LaTeX-source note for arXiv; handwritten-PDF
        // context for MIT OCW 8.513). Kept short — one sentence at most — so
        // we don't lecture about every format. Mockup §"Drop oversharing prose"
        // (delta 8): permitted only when the format genuinely needs context
        // the headline doesn't convey.
        let asideHtml = "";
        if (id === "mit-ocw-8513-quantum-lec3") {
            asideHtml = `<p class="std-rationale">Handwritten and doc-cam PDFs are an accessibility hard case. The page content starts out as a raster image — without remediation, it reads to a screen reader as a featureless rectangle. DocAble transforms them into accessible documents, with a transcription and navigable tags for figures, equations, paragraphs, lists, etc.</p>`;
        }
        else if (id === "arxiv-2208.03516") {
            asideHtml = `<p class="std-rationale">This paper was generated by <strong>LaTeX</strong> with an ACM template — a common scenario where the tag tree is either absent or structurally incomplete.</p>`;
        }
        else if (id === "edu-lecture-audio-captions") {
            asideHtml = `<p class="std-rationale">This slide embeds a spoken-audio clip with <strong>no caption track</strong> — its content is lost to a screen-reader user and to anyone with the sound off. DocAble transcribes the speech and writes a WebVTT caption track (<code>trackN.vtt</code>) back into the deck, so the spoken content is available as synchronized captions.</p>`;
        }
        const article = document.createElement("article");
        article.className = "showcase-example";
        article.id = `analysis-${id}`;
        article.setAttribute("aria-labelledby", `atitle-${id}`);
        article.innerHTML = `
      <header class="example-head">
        <span class="example-num" aria-hidden="true" data-seq>${fileIcon}</span>
        <div class="example-titles">
          <h2 id="atitle-${escAttr(id)}">${escHtml(title)}</h2>
          <p class="example-meta">${pages} ${escHtml(unitWord(format, pages))} &middot; ${escHtml(formatLabel(format))}${source ? ` &middot; ${escHtml(source)}` : ""}</p>
        </div>
        <div class="example-downloads">
          <a class="btn btn-outline" href="/api/examples/${encodeURIComponent(id)}/before" download aria-label="Before for ${escHtml(title)}">Before</a>
          <a class="btn btn-primary" href="/api/examples/${encodeURIComponent(id)}/after" download aria-label="After (remediated) for ${escHtml(title)}">After (remediated)</a>
          ${editorBtn}
          <a href="/api/examples/${encodeURIComponent(id)}/changes" class="btn btn-link" data-changes-link="${escAttr(id)}" aria-label="Download full changelog for ${escHtml(title)}">Download full changelog</a>
          <a href="#main" class="back-link" aria-label="↑ Back to top from ${escHtml(title)}">&uarr; Back to top</a>
        </div>
      </header>
      <div class="example-body">
        ${thumb}
        ${bodyEmptyHtml}
        ${defectSummaryHtml}
        ${asideHtml}
        ${remediationsHtml}
        ${awaitingHtml}
      </div>
    `;
        // Wire the changelog link to use the same buildChangelogText pipeline
        // (matches the per-card Changes button).
        const changesLink = article.querySelector("[data-changes-link]");
        if (changesLink) {
            changesLink.addEventListener("click", (ev) => handleChangesClick(ev, sample));
        }
        // Wire filter-chip click handlers for the awaiting section (if present).
        wireAwaitingFilters(article);
        return article;
    }
    // ─── Awaiting-section renderer + filter wiring ───────────────────────────
    //
    // Builds the Awaiting <details> for one fixture. Section structure:
    //   <details class="collapsible-section awaiting-section">
    //     <summary>⚠ Awaiting human intervention <badge>N items</badge></summary>
    //     <div class="collapsible-section-body">
    //       <div class="standards-filter-row" role="toolbar">
    //         [filter icon] Filter: <chip>...</chip>...
    //       </div>
    //       <div class="awaiting-group-label">Group A</div>
    //       <div class="awaiting-item" data-standards="...">...</div>
    //       ...
    //     </div>
    //   </details>
    //
    // The chip set is the union of every item's standards[] — built per-card.
    // When 0 standards are present we still render the section but skip the
    // filter row (no point filtering 0 chips).
    function renderAwaitingSection(sampleId, items) {
        // Union of standards across all items (preserves first-seen order).
        // Apply the sole-source filter PER-ITEM before unioning, so the chip
        // set the filter row exposes matches what each item's badges display.
        // Without this, a chip for "DocAble:X" could appear in the toolbar
        // when no item actually renders a DocAble badge (the filter would
        // hide everything when clicked — confusing).
        const seen = new Set();
        const allStandards = [];
        for (const it of items) {
            const list = filterClaimsForDisplay(Array.isArray(it.standards) ? it.standards : []);
            for (const s of list) {
                if (!seen.has(s)) {
                    seen.add(s);
                    allStandards.push(s);
                }
            }
        }
        // Filter row — chips are <button> for native keyboard activation.
        // aria-label includes sampleId so each chip has a UNIQUE accessible name —
        // U7-v3 uniqueness rule (axe a11y) fails when N awaiting sections each
        // render the same standard chip without disambiguation. Sighted users see
        // the chip text in its section context; AT users need the context inline.
        let filterRowHtml = "";
        if (allStandards.length > 0) {
            const chipsHtml = allStandards.map((s) => {
                const cls = classForStandard(s);
                const ariaLabel = `Filter ${s} open issues for ${sampleId}`;
                return `<button type="button" class="standards-badge ${cls}" data-filter-standard="${escAttr(s)}" aria-label="${escAttr(ariaLabel)}">${escHtml(s)}</button>`;
            }).join("");
            filterRowHtml = `
        <div class="standards-filter-row" role="toolbar" aria-label="Filter open issues by standard">
          <span class="filter-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
          </span>
          <span class="filter-label">Filter:</span>
          ${chipsHtml}
        </div>
      `;
        }
        // Group items by ``group`` field, preserving first-seen group order.
        const groupOrder = [];
        const byGroup = new Map();
        for (const it of items) {
            const g = it.group || "Other";
            let bucket = byGroup.get(g);
            if (!bucket) {
                bucket = [];
                byGroup.set(g, bucket);
                groupOrder.push(g);
            }
            bucket.push(it);
        }
        const itemsHtml = groupOrder.map((g) => {
            const groupItems = byGroup.get(g) || [];
            const itemHtmls = groupItems.map((it) => renderAwaitingItem(it)).join("");
            return `<div class="awaiting-group-label">${escHtml(g)}</div>${itemHtmls}`;
        }).join("");
        // a11y (Phase 6, a11y-name-and-selector-discipline-260629): same pattern as the
        // Remediations badge — role="status" + aria-label promotes the item count to a
        // named, queryable, announced a11y-tree node.
        const countText = `${items.length} item${items.length === 1 ? "" : "s"}`;
        const countBadge = `<span class="section-count-badge" role="status" aria-label="Awaiting: ${escAttr(countText)}">${escHtml(countText)}</span>`;
        // a11y (Phase 6, a11y-name-and-selector-discipline-260629): aria-label gives
        // the closed <details> an accessible name in Chromium's a11y tree (a closed
        // <details> does not expose its <summary> subtree as the group accname). The
        // label omits the ⚠ icon (decorative, aria-hidden) and the count badge (a
        // separate role="status" node) so the group name is the clean phrase a test
        // queries by and an AT user hears.
        //
        // a11y (Phase 22 U7-v3 fix, 260704): the SUMMARY control's accessible name
        // must be UNIQUE per card AND satisfy U7-v1 label-in-name — same two-constraint
        // fix as the Remediations summary above (6 fixtures render an Awaiting section,
        // so the fixed "Awaiting human intervention…" name is a page-wide U7-v3
        // duplicate). The visible label the predicate computes is "Awaiting human
        // intervention" + a space + the (role=status) count badge text (the ⚠ is
        // aria-hidden and excluded), so the summary aria-label reproduces that exact
        // prefix then appends " — {id}". Verified against the real predicate (pw 1.59).
        // The label still STARTS with "Awaiting human intervention" so the VC specs'
        // name:/Awaiting human intervention/i group + button locators still match.
        const awSummaryAccName = `Awaiting human intervention ${countText} — ${sampleId}`;
        return `
      <details class="collapsible-section awaiting-section" id="collapsible-awaiting-${escAttr(sampleId)}" aria-label="Awaiting human intervention — ${escAttr(sampleId)}">
        <summary role="button" aria-label="${escAttr(awSummaryAccName)}">
          <span class="awaiting-icon" aria-hidden="true">⚠</span>
          Awaiting human intervention
          ${countBadge}
        </summary>
        <div class="collapsible-section-body">
          ${filterRowHtml}
          <div class="awaiting-items-list" role="list" aria-label="Open issues requiring human review">
            ${itemsHtml}
          </div>
          <div class="awaiting-footer-note">
            These items were identified by DocAble's accessibility checker but could not be automatically resolved without authorial judgment. Download the full changelog (button above) for the complete machine-readable list, or open the file in its authoring tool to address each item directly.
          </div>
        </div>
      </details>
    `;
    }
    function renderAwaitingItem(item) {
        const rawStandards = Array.isArray(item.standards) ? item.standards : [];
        // Apply the sole-source display filter (Q2 settlement, 260513). When
        // the item carries both external and DocAble claims, suppress DocAble
        // from the rendered row. The DATA-standards attribute carries the
        // FILTERED list — the filter chips above only show what's displayed,
        // and the filter machinery must match what the user sees.
        const standards = filterClaimsForDisplay(rawStandards);
        const dataStandards = standards.join(",");
        const stdsHtml = standards.length > 0
            ? `<span class="awaiting-item-standards">${standards.map((s) => `<span class="standards-badge ${classForStandard(s)}">${escHtml(s)}</span>`).join("")}</span>`
            : "";
        return `
      <div class="awaiting-item" role="listitem" data-standards="${escAttr(dataStandards)}">
        <div class="awaiting-item-header">
          <span class="awaiting-item-loc">${escHtml(item.loc || "")}</span>
          <span class="awaiting-item-category">${escHtml(item.category || "")}</span>
          ${stdsHtml}
        </div>
        <div class="awaiting-item-desc">${escHtml(item.description || "")}</div>
        <div class="awaiting-item-reason"><strong>Why human review is needed:</strong> ${escHtml(item.reason || "")}</div>
      </div>
    `;
    }
    // Click handler: toggle .filter-active on the chip, then re-evaluate
    // every .awaiting-item under the same <details>: each item stays visible
    // if any active chip's standard appears in its data-standards (union).
    // No active chips = everything visible.
    function wireAwaitingFilters(scopeEl) {
        const sections = scopeEl.querySelectorAll(".awaiting-section");
        sections.forEach((section) => {
            const chips = section.querySelectorAll("button.standards-badge[data-filter-standard]");
            const items = section.querySelectorAll(".awaiting-item");
            if (chips.length === 0 || items.length === 0)
                return;
            chips.forEach((chip) => {
                chip.addEventListener("click", () => {
                    chip.classList.toggle("filter-active");
                    applyAwaitingFilters(section, chips, items);
                });
            });
        });
    }
    function applyAwaitingFilters(_section, chips, items) {
        const active = [];
        chips.forEach((c) => {
            if (c.classList.contains("filter-active")) {
                const std = c.getAttribute("data-filter-standard");
                if (std)
                    active.push(std);
            }
        });
        // awaitingItemMatchesFilters is a pure predicate imported from examples-logic.ts
        // (ts-coverage-260626 seam). It returns true when active is empty (show-all).
        //
        // a11y (Phase 6, a11y-name-and-selector-discipline-260629): filtered-out items
        // hide via the `.filtered-out` class, whose CSS rule is `display: none`
        // (examples.html). `display: none` is the canonical mechanism for removing an
        // element from BOTH layout AND the accessibility tree — an AT user never
        // perceives a filtered-out item. We deliberately do NOT also set `aria-hidden`:
        // toggling `aria-hidden` on an element already removed via `display: none` is
        // redundant (per WAI-ARIA, content hidden with `display: none` is already absent
        // from the accessibility tree), and the stray `aria-hidden` attribute was the
        // product gap that forced the spec to count hidden items through a raw CSS
        // attribute selector. With `display: none` as the sole hiding mechanism, the
        // still-visible items are countable through a genuine a11y-tree query —
        // `getByRole("listitem")` matches only accessibility-tree nodes, so filtered-out
        // (display:none) items naturally drop out of the count, no escape needed.
        items.forEach((it) => {
            const raw = it.getAttribute("data-standards") || "";
            const match = awaitingItemMatchesFilters(raw, active);
            it.classList.toggle("filtered-out", !match);
        });
    }
    // Fetch the batched changes map for ALL samples in ONE request.
    // Returns {sample_id: envelope} or {} on failure so every article
    // falls through to its empty-state shape. WHY one batch fetch instead
    // of an N+1 per-sample fan-out: the prior code issued one
    // /api/examples/{id}/changes fetch per sample (17 separate round-trips,
    // ~5s serial server-side on a cold instance — the perceived page
    // "hang"). The /api/examples/changes-batch endpoint assembles the whole
    // map server-side with a CONCURRENT thread-pool fetch + in-process
    // cache, so the browser pays one round-trip. RCA: docs/design/
    // p0-staging-web-bugs-rca-260626.md §1 RC-3.
    async function fetchAllSampleChanges() {
        try {
            const resp = await fetch("/api/examples/changes-batch", { cache: "no-cache" });
            if (!resp.ok)
                return {};
            const map = await resp.json();
            return (map && typeof map === "object") ? map : {};
        }
        catch (err) {
            // Per CLAUDE.md rule #8: not silent — we log and return {} so every
            // article falls through to the empty-state branch.
            window.adatool?.log?.warn("[examples] changes-batch fetch failed:", err);
            return {};
        }
    }
    // Render all expanded articles for the ordered sample list. The changes
    // envelopes are fetched in a SINGLE batched request (see
    // fetchAllSampleChanges) rather than N+1 per-sample fetches. The
    // per-article seq number is stamped after creation so the order matches
    // the on-page card-grid order.
    async function renderExpandedAnalyses(ordered) {
        const container = document.getElementById("expanded-analyses");
        if (!container)
            return;
        if (!ordered || ordered.length === 0)
            return;
        // Wait for rule metadata so per-change badges resolve. fetchRuleMetadata
        // is idempotent and cached on first call.
        await fetchRuleMetadata();
        const changesById = await fetchAllSampleChanges();
        const envelopes = ordered.map((s) => changesById[s.id] || null);
        const frag = document.createDocumentFragment();
        ordered.forEach((sample, idx) => {
            const article = renderExpandedArticle(sample, envelopes[idx] || null);
            // Stamp the 1-based sequence number on the example-num element so the
            // visible order matches the cards above.
            const numEl = article.querySelector("[data-seq]");
            if (numEl) {
                // Prepend "<n> " before the icon SVG (innerHTML already contains the icon).
                numEl.insertAdjacentText("afterbegin", `${idx + 1} `);
            }
            frag.appendChild(article);
        });
        container.appendChild(frag);
        container.hidden = false;
    }
    // ─── Provenance rendering ────────────────────────────────────────────────
    function renderProvenance(catalog) {
        const el = document.getElementById("examples-provenance");
        if (!el)
            return;
        const genAt = catalog.generated_at || "";
        // The promoted-at timestamp is intentionally not rendered. Mockup
        // §"Provenance line trim" (delta 11): the prod-promotion ceremony
        // is implementation detail; the user-meaningful signal is just
        // "when were these samples generated".
        let html = "";
        if (genAt) {
            html += `Samples generated <time datetime="${escAttr(genAt)}">${escHtml(fmtDate(genAt))}</time>`;
        }
        el.innerHTML = html;
    }
    // fmtDate imported from examples-format.ts.
    // ─── Catalog fetch + render ──────────────────────────────────────────────
    // Client-side catalog-fetch deadline. WHY: even with the server-side GCS
    // timeout, a serving instance that never responds (proxy stall, DNS, etc.)
    // would otherwise hang the catalog fetch — and with it the provenance
    // spinner — indefinitely. AbortController converts "never responds" into a
    // fast, legible "couldn't load" state. RCA: docs/design/
    // p0-staging-web-bugs-rca-260626.md §1 RC-2/§1.4-#3.
    const CATALOG_FETCH_TIMEOUT_MS = 8000;
    // Clear the "Loading sample provenance…" placeholder. Called on EVERY exit
    // path from loadCatalog — success, catalog error, or empty catalog — so the
    // served page never ends on a stuck spinner (CLAUDE.md rule #17: served-page
    // post-condition is a known content shape, never a perpetual placeholder).
    function clearProvenanceSpinner() {
        const el = document.getElementById("examples-provenance");
        if (el)
            el.innerHTML = "";
    }
    async function loadCatalog() {
        const grid = document.getElementById("examples-sample-grid");
        const unavailable = document.getElementById("examples-unavailable");
        if (!grid)
            return;
        let catalog;
        try {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), CATALOG_FETCH_TIMEOUT_MS);
            try {
                const resp = await fetch("/api/examples/catalog", { cache: "no-cache", signal: controller.signal });
                if (!resp.ok)
                    throw new Error(`HTTP ${resp.status}`);
                catalog = await resp.json();
            }
            finally {
                clearTimeout(timer);
            }
        }
        catch (err) {
            // Graceful degradation per design doc §"Cascading invariants" #4.
            // Always clear the spinner (RC-1): the prior code left
            // "#examples-provenance" on its "Loading…" placeholder when the catalog
            // fetch errored/aborted — the literal stuck-spinner symptom.
            window.adatool?.log?.warn("[examples] catalog unavailable:", err);
            clearProvenanceSpinner();
            if (unavailable) {
                unavailable.textContent = "We couldn’t load the sample list. Try refreshing the page.";
                unavailable.hidden = false;
            }
            return;
        }
        try {
            renderProvenance(catalog);
        }
        catch (err) {
            // A render exception (e.g. a malformed generated_at) must NOT leave the
            // spinner stuck. Log + clear so the page still reaches a known shape.
            window.adatool?.log?.warn("[examples] renderProvenance failed:", err);
            clearProvenanceSpinner();
        }
        const samples = Array.isArray(catalog.samples) ? catalog.samples : [];
        if (samples.length === 0) {
            if (unavailable) {
                unavailable.textContent = "No samples have been promoted yet. The next staging deploy will populate this page.";
                unavailable.hidden = false;
            }
            return;
        }
        // Group samples BY DOCUMENT KIND (documents / presentations / spreadsheets /
        // pdf, SSOT order) and render one <section data-kind> per non-empty kind
        // INSIDE the #examples-sample-grid wrapper (fold F-1 — the id stays on the
        // wrapper so the deploy canary + editor/e2e descendant selectors keep
        // matching). groupSamplesByKind is a pure, unit-tested helper derived from
        // the advertised-formats SSOT (kindForFormat lookup). Kind order controls
        // the expanded-analyses order below too (replaces the old FORMAT_RANK sort).
        const groups = groupSamplesByKind(samples);
        const ordered = [];
        for (const group of groups) {
            const section = document.createElement("section");
            section.className = "kind-section";
            section.setAttribute("data-kind", group.kind);
            section.setAttribute("aria-label", `${group.label} examples`);
            const heading = document.createElement("h2");
            heading.className = "kind-heading";
            heading.innerHTML = `${kindIconHtml(group.kind)}<span>${escHtml(group.label)}</span>`;
            section.appendChild(heading);
            const kindGrid = document.createElement("div");
            kindGrid.className = "sample-grid";
            kindGrid.setAttribute("role", "list");
            for (const s of group.samples) {
                kindGrid.appendChild(renderSampleCard(s));
                ordered.push(s);
            }
            section.appendChild(kindGrid);
            grid.appendChild(section);
        }
        // Render the expanded per-sample analyses below the grid. Fire-and-
        // forget — the cards are already visible; analyses populate as their
        // per-sample changes.json fetches resolve. Any failure inside renders
        // its empty-state branch rather than blocking the whole section.
        renderExpandedAnalyses(ordered).catch((err) => {
            window.adatool?.log?.warn("[examples] expanded analyses render failed:", err);
        });
    }
    // ─── Utilities ───────────────────────────────────────────────────────────
    // WHY: canonical HTML-escape lives in /static/esc.js (window.adatool.esc).
    // esc.js MUST be loaded before examples.js — enforced by script order in
    // examples.html. Both escHtml and escAttr contexts are safe with the same
    // full 5-character encode (& < > " ').
    // WHY non-null assertion: load order (examples.html script order) guarantees
    // esc.js is loaded before examples.js; this is a load-order contract, not
    // runtime discovery. A missing esc.js is a hard programmer error, not
    // graceful degradation territory.
    const escHtml = window.adatool?.esc ?? ((s) => s);
    const escAttr = window.adatool?.esc ?? ((s) => s);
    // ─── Changes link — fetch JSON, format as text, download ─────────────────
    //
    // Delegates to window.buildChangelogText (changelog-text.js, loaded before
    // this script). The envelope shape: { report: { total_items, categories: [...] } }.
    // See deploy/staging-smoke-emit-examples.py:generate_artifacts() for the
    // writer side; changelog-text.js §"minimal variant" for text conventions.
    function buildChangelogTextForSample(envelope, sample) {
        const r = (envelope && envelope.report) || {};
        // WHY non-null assertion: load order (examples.html script order) guarantees
        // changelog-text.js is loaded before examples.js. Missing is a programmer error.
        return window.buildChangelogText(r, {
            variant: "minimal",
            sample: sample,
            note: envelope && envelope.note,
        });
    }
    async function handleChangesClick(ev, sample) {
        ev.preventDefault();
        const id = sample.id;
        const url = `/api/examples/${encodeURIComponent(id)}/changes`;
        let envelope;
        try {
            const resp = await fetch(url, { cache: "no-cache" });
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
            envelope = await resp.json();
        }
        catch (err) {
            window.adatool?.log?.warn("[examples] changes fetch failed:", err);
            // Fall through: show a graceful message rather than crashing.
            envelope = { note: "The changelog artifact could not be fetched. Please try again later." };
        }
        const text = buildChangelogTextForSample(envelope, sample);
        const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = (sample.filename || sample.id || "sample") + "-changes.txt";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            URL.revokeObjectURL(a.href);
            a.remove();
        }, 0);
    }
    // ─── Public helpers (consumed by tests + the expanded-analyses renderer) ─
    //
    // The expanded-analyses panel renders per-change badges by joining
    // changes[].rule_ids[] with /api/rules/metadata. The join helpers stay
    // exposed on window.adaExamples so tests can vm-load and invoke them
    // without booting the full DOMContentLoaded path.
    // Wire the __test escape hatch (see module-level export above) so jsdom
    // pilot tests can call IIFE-private functions via production code paths.
    // Production code never reads __test; the object is tree-shaken in
    // any bundled build. Per ts-jsdom-coverage-260626 Phase 2 Pilot C.
    __test.renderSampleCard = renderSampleCard;
    __test.loadCatalog = loadCatalog;
    window.adaExamples = {
        fetchRuleMetadata,
        standardsForRuleIds,
        rollupStandardsByTarget,
        targetLabel,
        defectTier,
        renderExpandedArticle,
        renderAwaitingSection,
        classForStandard,
        filterClaimsForDisplay,
        renderStandardsBadgeRow,
        remediationClaimsFor,
        AWAITING_ITEMS_BY_FIXTURE,
        REMEDIATION_CLAIMS_BY_FIXTURE,
        // Exposed for seam tests: the catalog loader + its always-clear-spinner
        // helper, so the BUG-1 spinner-clear-on-error path is directly testable
        // without booting the full DOMContentLoaded path.
        loadCatalog,
        clearProvenanceSpinner,
    };
    // ─── Boot ────────────────────────────────────────────────────────────────
    function boot() {
        // Render the "How to use" supported-format chips (grouped by kind) from the
        // shared SSOT renderer. No-ops if the container is absent (defensive).
        try {
            renderSupportedFormatBubblesInto("examples-supported-formats");
        }
        catch (err) {
            window.adatool?.log?.warn("[examples] supported-format bubbles render failed:", err);
        }
        // Prime the rule-metadata cache early so any expanded-analyses interaction
        // doesn't pay the round-trip on first click. Errors are swallowed; the
        // fallback (Phase-1 format-level badges) lives in standardsForRuleIds().
        fetchRuleMetadata();
        loadCatalog();
    }
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    }
    else {
        boot();
    }
}());
